import { JobillaLogo } from '@/components'
import { useJobillaOrgId } from '@/hooks'
import { ConfirmationDialog, type ConfirmationDialogRef, SubtleLoader, Tooltip } from '@/ui'
import { type AdDesignData, useAdDesignIndex } from '@jobilla/nexus-sdk'
import { ImageIcon } from 'lucide-react'
import { useRef } from 'react'
import { initialCanvasData } from '../canvas'

export interface DesignFilmstripProps {
	onApplyDesign: (document: string) => void
}

export function DesignFilmstrip({ onApplyDesign }: DesignFilmstripProps) {
	const organisationId = useJobillaOrgId()
	const { data: designs, isLoading } = useAdDesignIndex(organisationId ?? '')
	const confirmRef = useRef<ConfirmationDialogRef>(null)

	const handleApply = async (design: AdDesignData) => {
		try {
			await confirmRef.current?.confirm({
				title: 'Apply design',
				message: `Apply "${design.name}"? This will replace your current design.`,
				confirmText: 'Apply',
			})
			onApplyDesign(design.document)
		} catch {
			// cancelled
		}
	}

	const handleApplyDefault = async () => {
		try {
			await confirmRef.current?.confirm({
				title: 'Reset design',
				message: 'Reset design to the Jobilla default?',
				confirmText: 'Reset',
			})
			onApplyDesign(JSON.stringify(initialCanvasData))
		} catch {
			// cancelled
		}
	}

	if (isLoading) {
		return (
			<div className="p-3">
				<SubtleLoader>Loading designs</SubtleLoader>
			</div>
		)
	}

	return (
		<div className="p-2 space-y-2">
			<div className="flex flex-wrap gap-2">
				<Tooltip content="Jobilla default">
					<button
						type="button"
						className="group flex flex-col items-center gap-1 rounded-lg border border-grey-200 p-1.5 hover:border-brand-300 hover:bg-brand-50/50 transition-colors w-[80px] cursor-pointer"
						onClick={handleApplyDefault}
					>
						<div className="w-full aspect-[4/5] rounded bg-grey-50 overflow-hidden flex items-center justify-center">
							<JobillaLogo />
						</div>
						<span className="text-[10px] text-grey-600 truncate w-full text-center">Jobilla default</span>
					</button>
				</Tooltip>
				{(designs ?? []).map((design) => (
					<Tooltip key={design.id} content={design.name}>
						<button
							type="button"
							className="group flex flex-col items-center gap-1 rounded-lg border border-grey-200 p-1.5 hover:border-brand-300 hover:bg-brand-50/50 transition-colors w-[80px] cursor-pointer"
							onClick={() => handleApply(design)}
						>
							<div className="w-full aspect-[4/5] rounded bg-grey-50 overflow-hidden flex items-center justify-center">
								{design.thumbnailUrl
									? (
										<img
											src={design.thumbnailUrl}
											alt={design.name}
											className="w-full h-full object-cover"
										/>
									)
									: <ImageIcon className="size-4 text-grey-300" />}
							</div>
							<span className="text-[10px] text-grey-600 truncate w-full text-center">{design.name}</span>
						</button>
					</Tooltip>
				))}
			</div>
			<ConfirmationDialog ref={confirmRef} />
		</div>
	)
}
